import { ArrowRight, FileUp, Settings, Send, Video, BarChart3, Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';

const HowItWorks = () => {
  const { t } = useTranslation();
  const [cvVisible, setCvVisible] = useState(false);
  const [intVisible, setIntVisible] = useState(false);
  const cvRef = useRef(null);
  const intRef = useRef(null);

  useEffect(() => {
    const cvObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setCvVisible(true);
      },
      { threshold: 0.2 }
    );

    const intObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIntVisible(true);
      },
      { threshold: 0.2 }
    );

    if (cvRef.current) cvObserver.observe(cvRef.current);
    if (intRef.current) intObserver.observe(intRef.current);

    return () => {
      cvObserver.disconnect();
      intObserver.disconnect();
    };
  }, []);

  const cvScoringSteps = [
    {
      number: '1',
      icon: <FileUp className="w-6 h-6" />,
      title: t('howItWorks.cvStep1Title'),
      description: t('howItWorks.cvStep1Desc'),
    },
    {
      number: '2',
      icon: <Eye className="w-6 h-6" />,
      title: t('howItWorks.cvStep2Title'),
      description: t('howItWorks.cvStep2Desc'),
    },
    {
      number: '3',
      icon: <Send className="w-6 h-6" />,
      title: t('howItWorks.cvStep3Title'),
      description: t('howItWorks.cvStep3Desc'),
    },
  ];

  const interviewSteps = [
    {
      number: '1',
      icon: <Settings className="w-6 h-6" />,
      title: t('howItWorks.intStep1Title'),
      description: t('howItWorks.intStep1Desc'),
    },
    {
      number: '2',
      icon: <Send className="w-6 h-6" />,
      title: t('howItWorks.intStep2Title'),
      description: t('howItWorks.intStep2Desc'),
    },
    {
      number: '3',
      icon: <BarChart3 className="w-6 h-6" />,
      title: t('howItWorks.intStep3Title'),
      description: t('howItWorks.intStep3Desc'),
    },
  ];

  return (
    <section id="how-it-works" className="section-spacing bg-white overflow-x-hidden">
      <div className="container-custom px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 md:mb-20">
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-3 sm:mb-4">
            {t('howItWorks.title')}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-2">
            {t('howItWorks.subtitle')}
          </p>
        </div>

        {/* CV Scoring Section */}
        <div ref={cvRef} className="mb-16 sm:mb-20 md:mb-24">
          <div className={`flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8 md:mb-10 transition-all duration-600 ${
            cvVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center">
              <FileUp className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
              {t('howItWorks.cvScoringTitle')}
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {cvScoringSteps.map((step, index) => (
              <div 
                key={index} 
                className={`relative transition-all duration-700 ${
                  cvVisible 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-accent-teal text-white flex items-center justify-center font-bold text-xs sm:text-sm transition-all duration-500 ${
                      cvVisible ? 'scale-100 rotate-0' : 'scale-0 rotate-180'
                    }`} style={{ transitionDelay: `${index * 150 + 200}ms` }}>
                      {step.number}
                    </div>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-accent-teal/10 flex items-center justify-center text-accent-teal">
                      <div className="w-5 h-5 sm:w-6 sm:h-6">
                        {step.icon}
                      </div>
                    </div>
                  </div>
                  <h4 className="text-base sm:text-lg font-subheading text-gray-900 mb-1 sm:mb-2">{step.title}</h4>
                  <p className="text-gray-600 text-xs sm:text-sm">{step.description}</p>
                </div>
                {index < cvScoringSteps.length - 1 && (
                  <div className={`hidden md:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10 transition-all duration-500 ${
                    cvVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                  }`} style={{ transitionDelay: `${index * 150 + 300}ms` }}>
                    <ArrowRight className="w-6 h-6 text-accent-teal" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Interview Section */}
        <div ref={intRef}>
          <div className={`flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8 md:mb-10 transition-all duration-600 ${
            intVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-accent-teal/10 flex items-center justify-center">
              <Video className="w-5 h-5 sm:w-6 sm:h-6 text-accent-teal" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
              {t('howItWorks.interviewTitle')}
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {interviewSteps.map((step, index) => (
              <div 
                key={index} 
                className={`relative transition-all duration-700 ${
                  intVisible 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 h-full border-2 border-transparent hover:border-primary/20 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xs sm:text-sm transition-all duration-500 ${
                      intVisible ? 'scale-100 rotate-0' : 'scale-0 rotate-180'
                    }`} style={{ transitionDelay: `${index * 150 + 200}ms` }}>
                      {step.number}
                    </div>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                      <div className="w-5 h-5 sm:w-6 sm:h-6">
                        {step.icon}
                      </div>
                    </div>
                  </div>
                  <h4 className="text-base sm:text-lg font-subheading text-gray-900 mb-1 sm:mb-2">{step.title}</h4>
                  <p className="text-gray-600 text-xs sm:text-sm">{step.description}</p>
                </div>
                {index < interviewSteps.length - 1 && (
                  <div className={`hidden md:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10 transition-all duration-500 ${
                    intVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                  }`} style={{ transitionDelay: `${index * 150 + 300}ms` }}>
                    <ArrowRight className="w-6 h-6 text-primary" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
